import { ThemeContext } from '@/theme/ThemeProvider.tsx';
import { ConfigProvider, Spin } from 'antd';
import React, { lazy, Suspense, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import 'react-loading-skeleton/dist/skeleton.css';
import { BrowserRouter, Routes as ReactRoutes, Route } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { darkTheme, lightTheme } from '../styles/Qtheme';
import { View } from '../styles/reactnative';
import { GlobalStyle } from './../styles/global-styles';
import { NotFoundPage } from './components/NotFoundPage/Loadable';
import Layout from './components/QBIT/Layout';
import { useToken } from './hooks';
import AdditionalInfoPage from './pages/AdditionalInfoPage';
import Logout from './pages/Logout';
import * as PAGE from './pagesImport';
import ScrollToTop from '@utils/ScrollToTop.tsx';
import UnloadLog from '@/utils/UnloadLog';
import CRenderEmpty from '@components/CRenderEmpty';
import { NoticePage } from '@app/pages/NoticePage/Loadable.tsx';
import { NoticeArticlePage } from '@app/pages/NoticeArticlePage/Loadable.tsx';
import { MMRPage } from '@app/pages/MMRPage';

const LoadingFallback: React.FC<{ text?: string }> = ({ text }) => {
  const { isDarkMode } = useContext(ThemeContext);
  return (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      minHeight: '200px',
      padding: '324px 0',
      gap: 12,
      color: isDarkMode ? '#fff' : '#000',
    }}
  >
    <Spin size="large" />
    {text ? <div style={{ fontSize: 14, opacity: 0.7, color: isDarkMode ? '#fff' : '#000' }}>{text}</div> : null}
  </div>
);
};

const QHomePage = lazy(() =>
  import('./pages/QBIT/QHomePage').then((module) => ({
    default: module.QHomePage,
  }))
);
const Qp2pPage = lazy(() =>
  import('./pages/QBIT/Qp2pPage').then((module) => ({
    default: module.Qp2pSection,
  }))
);
const AboutUsSection = lazy(() =>
  import('./pages/QBIT/AboutUs').then((module) => ({
    default: module.AboutUsSection,
  }))
);
const OriginDepositSection = lazy(() =>
  import('./pages/QBIT/Deposit').then((module) => ({
    default: module.OriginDepositSection,
  }))
);
const DepositPage = lazy(() =>
  import('./pages/QBIT/Deposit').then((module) => ({
    default: module.OriginDepositSection,
  }))
);
const MarketSection = lazy(() =>
  import('./pages/QBIT/Market').then((module) => ({
    default: module.MarketSection,
  }))
);
const BotPayment = lazy(() =>
  import('./pages/QBIT/BotPayment').then((module) => ({
    default: module.BotPayment,
  }))
);
const CopyTradePage = lazy(() =>
  import('./pages/CopyTradePage').then((module) => ({
    default: module.CopyTradePage,
  }))
);
const LoanSection = lazy(() =>
  import('./pages/QBIT/Loan').then((module) => ({
    default: module.LoanSection,
  }))
);
const Fund = lazy(() =>
  import('./pages/QBIT/Fund').then((module) => ({
    default: module.Fund,
  }))
);
const Pay = lazy(() =>
  import('./pages/QBIT/Pay').then((module) => ({
    default: module.Pay,
  }))
);
const Earn = lazy(() =>
  import('./pages/QBIT/Earn').then((module) => ({
    default: module.Earn,
  }))
);
const Convert = lazy(() =>
  import('./pages/QBIT/Convert').then((module) => ({
    default: module.Convert,
  }))
);
const TermsSection = lazy(() =>
  import('./pages/QBIT/Terms').then((module) => ({
    default: module.TermsSection,
  }))
);
const GuideSection = lazy(() =>
  import('./pages/QBIT/Guide').then((module) => ({
    default: module.Guide,
  }))
);
const InstitutionalSection = lazy(() =>
  import('./pages/QBIT/Institutional').then((module) => ({
    default: module.InstitutionalSection,
  }))
);
const ZMobileTradingView = lazy(() =>
  import('./pages/ZMobileTradingView').then((module) => ({
    default: module.ZMobileTradingView,
  }))
);
const TradePage = lazy(() =>
  import('./pages/TradePage').then((module) => ({
    default: module.TradePage,
  }))
);
const TradeCompetition = lazy(() =>
  import('./pages/TradeCompetition').then((module) => ({
    default: module.TradeCompetition,
  }))
);
const ReferralPage = lazy(() =>
  import('./pages/ReferralPage').then((module) => ({
    default: module.ReferralSection,
  }))
);
const OrdersPage = lazy(() =>
  import('./pages/OrdersPage').then((module) => ({
    default: module.OrdersPage,
  }))
);
const TradingBotPage = lazy(() =>
  import('./pages/TradingBotPage').then((module) => ({
    default: module.TradingBotPage,
  }))
);
const AssetPage = lazy(() =>
  import('./pages/AssetPage').then((module) => ({
    default: module.AssetPage,
  }))
);
const ProfileSettingPage = lazy(() => import('./pages/ProfileSetting'));
const BuySellCryptoPage = lazy(() =>
  import('./pages/QBIT/BuySellCrypto').then((module) => ({
    default: module.BuySellCryptoPage,
  }))
);
const BuyCryptoPage = lazy(() =>
  import('./pages/QBIT/BuyCrypto').then((module) => ({
    default: module.BuyCryptoPage,
  }))
);
const PnlCalendarPage = lazy(() =>
  import('./pages/PnlCalendarPage').then((module) => ({
    default: module.PnlCalendarPage,
  }))
);
const FearGreedIndexPage = lazy(() =>
  import('./pages/FearGreedIndex').then((module) => ({
    default: module.default,
  }))
);

export function Routes() {
  const { i18n, t } = useTranslation();
  const { data: token } = useToken();
  const { isDarkMode, toggleTheme } = useContext(ThemeContext);
  const theme = isDarkMode ? 'dark' : 'light';

  const useragt = navigator.userAgent.toLowerCase();
  if (useragt.match(/kakaotalk/i)) {
    return (
      <View
        style={{
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          fontSize: 20,
        }}
      >
        Do not support KakaoTalk Browser
      </View>
    );
  }

  const themeToggler = () => {
    // setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
    toggleTheme();
  };

  return (
    <BrowserRouter>
      <UnloadLog />
      <ScrollToTop />
      <Helmet
        defaultTitle={'Bynova'}
        titleTemplate="%s"
        htmlAttributes={{ lang: i18n.language }}
      >
        <meta name="description" content={t('title')} />
      </Helmet>
      <ConfigProvider
        theme={{
          components: {
            Message: {
              zIndexPopup: 20000,
            },
            Radio: {
              buttonBg: 'transparent',
              buttonColor: theme === 'light' ? '#212121' : '#fff',
            },
            Table: {
              headerBg: theme === 'light' ? '#F6EDEA' : '#191A19',
              headerColor: theme === 'light' ? '#212121' : '#fff',
              headerBorderRadius: 0,
              headerSplitColor: 'transparent',
              footerBg: theme === 'light' ? '#F6EDEA' : '#191A19',
              stickyScrollBarBg: theme === 'light' ? '#F6EDEA' : '#191A19',
              borderColor: 'transparent',
            },
            Select: {
              optionActiveBg: theme === 'light' ? '#EFD9D0' : '#333333',
              optionSelectedBg: theme === 'light' ? '#EFD9D0' : '#333333',
              selectorBg: theme === 'light' ? '#F6EDEA' : '#191A19',
            },
          },
          token: {
            // Seed Token
            colorPrimary: '#FF5C17',
            colorPrimaryHover: '#e96f3e',
            colorPrimaryActive: '#d76331',
            colorBorder: '#f78e61',
            colorBgContainer: theme === 'light' ? '#F6EDEA' : '#191A19',
            colorText: theme === 'light' ? '#212121' : '#fff',
            colorBgBase: theme === 'light' ? '#F6EDEA' : '#474747',
            colorTextPlaceholder: theme === 'light' ? '#757575' : '#B5B5BC',
            colorTextDisabled: theme === 'light' ? '#212121' : 'gray',
          },
        }}
      >
        <ThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
          <GlobalStyle />
          <Layout themeToggler={themeToggler}>
            <Suspense fallback={<LoadingFallback />}> 
              <ReactRoutes>
                <Route
                  path="asset"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Assets..." />}>
                      {token ? <AssetPage /> : <PAGE.QLoginPage />}
                    </Suspense>
                  }
                />

                <Route
                  path="/"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Home..." />}>
                      <QHomePage />
                    </Suspense>
                  }
                />
                {/* P2P Page */}
                <Route
                  path="p2p"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading p2p..." />}>
                      <Qp2pPage />
                    </Suspense>
                  }
                />
                <Route
                  path="about-us"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading About Us..." />}>
                      <AboutUsSection />
                    </Suspense>
                  }
                />

                {/* Deposit one-click-buy Page */}
                <Route
                  path="crypto"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Origin Deposit..." />}>
                      <OriginDepositSection />
                    </Suspense>
                  }
                />
                {/*<Route*/}
                {/*  path="one-click-buy"*/}
                {/*  element={*/}
                {/*    <Suspense fallback={<div>Loading Deposit...</div>}>*/}
                {/*      <DepositPage />*/}
                {/*    </Suspense>*/}
                {/*  }*/}
                {/*/>*/}

                {/* market Page */}
                <Route
                  path="markets"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Market..." />}>
                      <MarketSection />
                    </Suspense>
                  }
                />

                {/* Bot Payment Page */}
                <Route
                  path="bot-payment"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Bot Payment..." />}>
                      <BotPayment />
                    </Suspense>
                  }
                />

                {/* Card Traiding Page */}
                <Route
                  path="copytrade"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Copy Trade..." />}>
                      <CopyTradePage />
                    </Suspense>
                  }
                />

                {/* <Route path="crypto" element={<CryptoDepositPage/>}/> */}

                {/* Loan Page */}
                <Route
                  path="loan"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Loan..." />}>
                      <LoanSection />
                    </Suspense>
                  }
                />
                <Route
                  path="funds"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Fund..." />}>
                      <Fund />
                    </Suspense>
                  }
                />

                {/* Pay Page */}
                <Route
                  path="pay"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Pay..." />}>
                      <Pay />
                    </Suspense>
                  }
                />

                {/* Earn Page */}
                <Route
                  path="earn"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Earn..." />}>
                      <Earn />
                    </Suspense>
                  }
                />

                {/* Convert Page */}
                <Route
                  path="convert"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Convert..." />}>
                      <Convert />
                    </Suspense>
                  }
                />

                <Route
                  path="terms-and-conditions"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Terms..." />}>
                      <TermsSection />
                    </Suspense>
                  }
                />

                <Route
                  path="guide"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Guide..." />}>
                      <GuideSection />
                    </Suspense>
                  }
                />

                <Route
                  path="guide/:id"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Guide Detail..." />}>
                      <GuideSection />
                    </Suspense>
                  }
                />

                <Route
                  path="institutional"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Institutional..." />}>
                      <InstitutionalSection />
                    </Suspense>
                  }
                />

                <Route
                  path="leverage-margin"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Market..." />}>
                      <MMRPage />
                    </Suspense>
                  }
                />

                <Route path={'trade'}>
                  <Route
                    path="chart"
                    element={
                      <Suspense
                        fallback={<LoadingFallback text="Loading ZMobile Trading..." />}
                      >
                        <ZMobileTradingView />
                      </Suspense>
                    }
                  >
                    <Route
                      path="*"
                      element={
                        <Suspense
                          fallback={<LoadingFallback text="Loading ZMobile Trading..." />}
                        >
                          <ZMobileTradingView />
                        </Suspense>
                      }
                    />
                  </Route>
                </Route>

                {/*<Route path="/trade/chart" element={<PAGE.ZMobileTradingView />} />*/}
                {/*<Route*/}
                {/*  path="/trade/chart/*"*/}
                {/*  element={<PAGE.ZMobileTradingView />}*/}
                {/*/>*/}
                {/* Security Page */}
                {/* Primary Page */}
                {/*<Route exact path="/" component={PAGE.HomePage} />*/}

                {/* Authentication Page */}
                <Route
                  path="login"
                  element={token ? <ProfileSettingPage /> : <PAGE.QLoginPage />}
                />
                <Route
                  path="logout"
                  element={token ? <Logout /> : <PAGE.QLoginPage />}
                />
                <Route
                  path="signup"
                  element={token ? <ProfileSettingPage /> : <PAGE.QJoinPage />}
                />
                {/* Authentication Page */}

                {/* Trading View Page */}
                <Route
                  path="trade/:ep_id"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Trade..." />}>
                      <TradePage />
                    </Suspense>
                  }
                />
                <Route
                  path="trade-competition/:id"
                  element={
                    <Suspense
                      fallback={<LoadingFallback text="Loading Trade Competition..." />}
                    >
                      <TradeCompetition />
                    </Suspense>
                  }
                />
                {/*<Route*/}
                {/*  path="trade"*/}
                {/*  element={*/}
                {/*    <Suspense fallback={<div>Loading Trade...</div>}>*/}
                {/*      <TradePage />*/}
                {/*    </Suspense>*/}
                {/*  }*/}
                {/*>*/}
                {/*  <Route*/}
                {/*    path="*"*/}
                {/*    element={*/}
                {/*      <Suspense fallback={<div>Loading Trade...</div>}>*/}
                {/*        <TradePage />*/}
                {/*      </Suspense>*/}
                {/*    }*/}
                {/*  />*/}
                {/*</Route>*/}
                {/* Trading View Page */}

                {/* Markets  Page */}
                {/*<Route*/}
                {/*	exact*/}
                {/*	path="/markets/"*/}
                {/*	component={PAGE.MarketPage}*/}
                {/*/>*/}
                {/*<Route path="/markets/trends" element={<PAGE.MarketTrendPage />} />*/}
                {/*<Route path="markets">*/}
                {/*  <Route path="trends" element={<PAGE.MarketTrendPage />} />*/}
                {/*</Route>*/}
                {/* Markets  Page */}
                <Route
                  path="referral"
                  element={
                    <Suspense fallback={<div>Loading Referral...</div>}>
                      {token ? <ReferralPage /> : <PAGE.QLoginPage />}
                    </Suspense>
                  }
                />

                {/* Orders Page */}
                <Route
                  path="orders"
                  element={
                    <Suspense fallback={<div>Loading Orders...</div>}>
                      <OrdersPage />
                    </Suspense>
                  }
                />
                <Route
                  path="tradingbot"
                  element={
                    <Suspense fallback={<div>Loading Trading Bot...</div>}>
                      <TradingBotPage />
                    </Suspense>
                  }
                />
                {/*<Route path="leaderboard" element={<PAGE.LeaderBoardPage />} />*/}
                {/*<Route path="copytrade" element={<PAGE.CopyTradePage />} />*/}
                {/*<Route*/}
                {/*  path="followertrader"*/}
                {/*  element={<PAGE.FollowerTraderPage />}*/}
                {/*/>*/}
                {/*<Route*/}
                {/*  path="traderdetail"*/}
                {/*  element={<PAGE.TraderDetailPage />}*/}
                {/*/>*/}

                {/*<Route*/}
                {/*  path="notifications"*/}
                {/*  element={<PAGE.NotificationPage />}*/}
                {/*/>*/}
                {/*<Route path="notiSetting" element={<PAGE.NotiSettingPage />} />*/}
                {/*<Route path="abouts" element={<PAGE.AboutsPage />} />*/}
                {/*<Route path="supports" element={<PAGE.SupportsPage />} />*/}
                <Route
                  path="settings"
                  element={token ? <ProfileSettingPage /> : <PAGE.QLoginPage />}
                />
                {/* secoundary Page */}
                {/* <Route exact path="/about/" component={PAGE.AboutPage} /> */}
                {/*<Route path="contactus" element={<PAGE.ContactUsPage />} />*/}
                {/*<Route path="faq" element={<PAGE.FAQPage />} />*/}
                {/*<Route*/}
                {/*  path="terms-service"*/}
                {/*  element={<PAGE.PrivacyPolicyPage />}*/}
                {/*/>*/}
                <Route
                  path="additional-info"
                  element={
                    <Suspense fallback={<LoadingFallback />}> 
                      <AdditionalInfoPage />
                    </Suspense>
                  }
                />
                <Route
                  path="buysell-crypto"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Buy Crypto..." />}>
                      <BuySellCryptoPage />
                    </Suspense>
                  }
                />
                <Route
                  path="buy-crypto"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Buy Crypto..." />}>
                      <BuyCryptoPage />
                    </Suspense>
                  }
                />

                <Route
                  path="notice"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Notice..." />}>
                      <NoticePage />
                    </Suspense>
                  }
                />

                <Route
                  path="notice/article/:category/:id"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Notice Articel..." />}>
                      <NoticeArticlePage />
                    </Suspense>
                  }
                />

                <Route
                  path="pnl-calendar"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading PnL Calendar..." />}>
                      {token ? <PnlCalendarPage /> : <PAGE.QLoginPage />}
                    </Suspense>
                  }
                />

                <Route
                  path="fear-greed-index"
                  element={
                    <Suspense fallback={<LoadingFallback text="Loading Fear & Greed Index..." />}>
                      <FearGreedIndexPage />
                    </Suspense>
                  }
                />

                <Route path="*" element={<NotFoundPage />} />
              </ReactRoutes>
            </Suspense>
          </Layout>
        </ThemeProvider>
      </ConfigProvider>
    </BrowserRouter>
  );
}
